/**
 * HealthScoreBadge Component
 *
 * Displays API health score as a color-coded badge with compliance/quality breakdown.
 * Tooltip opens on hover of the badge (Bootstrap tooltip pattern).
 *
 * Props:
 *   - score: number (0-100)
 *   - breakdown: { compliance: number, quality: number }
 *   - lastCalculated?: string (ISO timestamp)
 */

import React, { useEffect, useRef } from 'react';
import { Tooltip } from 'bootstrap';
import './HealthScoreBadge.css';

interface HealthScoreBadgeProps {
    score: number;
    breakdown: {
        compliance: number;
        quality: number;
    };
    lastCalculated?: string;
}

/**
 * Get badge color and label based on score
 * Color coding: 80+ green, 60-79 yellow, 40-59 orange, 0-39 red
 */
function getScoreInfo(score: number): { bgColor: string; label: string } {
    if (score >= 80) {
        return { bgColor: '#73ad76', label: 'Healthy' };
    } else if (score >= 60) {
        return { bgColor: '#cfb12d', label: 'Acceptable' };
    } else if (score >= 40) {
        return { bgColor: '#FFA726', label: 'Degraded' };
    } else {
        return { bgColor: '#E57373', label: 'Critical' };
    }
}

/**
 * Format timestamp for display
 */
function formatTimestamp(iso?: string): string {
    if (!iso) return '';
    try {
        const date = new Date(iso);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
        return '';
    }
}

const HealthScoreBadge: React.FC<HealthScoreBadgeProps> = ({
    score,
    breakdown,
    lastCalculated,
}) => {
    const info = getScoreInfo(score);
    const formattedDate = lastCalculated ? formatTimestamp(lastCalculated) : '';
    const badgeRef = useRef<HTMLSpanElement>(null);

    const tooltipText = `<strong>Score:</strong> ${score}/100<br><strong>Compliance:</strong> ${breakdown.compliance}%<br><strong>Quality:</strong> ${breakdown.quality}%${formattedDate ? `<br><strong>Updated:</strong> ${formattedDate}` : ''}`;

    useEffect(() => {
         const el = badgeRef.current;
         if (!el) return;
         // Recreate tooltip when content changes, but always dispose the previous instance.
         Tooltip.getInstance(el)?.dispose();
         const tooltip = new Tooltip(el);
         return () => {
             tooltip.dispose();
         };
     }, [tooltipText]);


    return (
        <span
            ref={badgeRef}
            className="health-score-badge"
            style={{ backgroundColor: info.bgColor }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            data-bs-custom-class="custom-tooltip health-score-tooltip"
            data-bs-html="true"
            data-bs-title={tooltipText}
        >
            <strong>{score}%</strong>
            <span className="health-score-badge-label">{info.label}</span>
        </span>
    );
};

export default HealthScoreBadge;
